import { cn } from "@acme/ui";
import Callout from "@acme/ui/callout";
import { File, Files, Folder } from "@acme/ui/files";
import { Image } from "@unpic/react";
import {
  type AnchorHTMLAttributes,
  ComponentProps,
  ComponentPropsWithoutRef,
  type HTMLAttributes,
  type ImgHTMLAttributes,
  lazy,
} from "react";

// biome-ignore lint/suspicious/noExplicitAny: MDX components need flexible typing
export const components: Record<string, React.ComponentType<any> | undefined> =
  {
    h1: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h1
        className={cn(
          "mt-2 scroll-m-20 font-bold text-4xl tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    h2: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h2
        className={cn(
          "mt-10 scroll-m-28 border-b pb-1 font-semibold text-3xl tracking-tight first:mt-0",
          className
        )}
        {...props}
      />
      // @end
    ),
    h3: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h3
        className={cn(
          "mt-8 scroll-m-28 font-semibold text-2xl tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    h4: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h4
        className={cn(
          "mt-8 scroll-m-28 font-semibold text-xl tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    h5: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h5
        className={cn(
          "mt-8 scroll-m-20 font-semibold text-lg tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    h6: ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      // @sonar-ignore
      <h6
        className={cn(
          "mt-8 scroll-m-20 font-semibold text-base tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    a: ({ className, ...props }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
      // @sonar-ignore
      <a
        className={cn("font-medium underline underline-offset-4", className)}
        href={props.href as string}
        target={props.href?.startsWith("http") ? "_blank" : undefined}
        {...props}
      />
      // @end
    ),
    p: ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
      <p
        className={cn("leading-8 [&:not(:first-child)]:mt-6", className)}
        {...props}
      />
    ),
    ul: ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
      <ul className={cn("my-6 ml-6 list-disc", className)} {...props} />
    ),
    ol: ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
      <ol className={cn("my-6 ml-6 list-decimal", className)} {...props} />
    ),
    li: ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
      <li className={cn("mt-2", className)} {...props} />
    ),
    blockquote: ({ className, ...props }: HTMLAttributes<HTMLElement>) => (
      <blockquote
        className={cn(
          "my-6 border-l-4 pl-6 italic [&>*]:text-gray-800 dark:[&>*]:text-gray-300",
          className
        )}
        {...props}
      />
    ),
    img: ({
      className,
      alt,
      src,
      width,
      height,
      ...props
    }: ImgHTMLAttributes<HTMLImageElement>) => (
      <Image
        alt={alt}
        className={cn("rounded-md border", className)}
        height={height as number}
        layout="constrained"
        src={src as string}
        width={width as number}
        {...props}
      />
    ),
    hr: ({ ...props }) => <hr className="my-4 md:my-8" {...props} />,
    table: ({ className, ...props }: HTMLAttributes<HTMLTableElement>) => (
      <div className="my-6 w-full overflow-y-auto">
        <table className={cn("w-full", className)} {...props} /> {/* NOSONAR */}
      </div>
    ),
    tr: ({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) => (
      <tr
        className={cn("m-0 border-t p-0 even:bg-muted", className)}
        {...props}
      />
    ),
    th: ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
      <th
        className={cn(
          "border px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right",
          className
        )}
        {...props}
      />
    ),
    td: ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
      <td
        className={cn(
          "border px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right",
          className
        )}
        {...props}
      />
    ),
    pre: lazy(() => import("@acme/ui/code-block")),
    figcaption: lazy(() => import("@acme/ui/code-block-header")),
    code: ({ className, ...props }: ComponentPropsWithoutRef<"code">) => (
      <code {...props} />
    ),
    steps: ({ ...props }) => (
      <div className="steps mb-12 ml-8 border-l pl-8" {...props} />
    ),
    step: ({ className, ...props }: ComponentProps<"h3">) => (
      // @sonar-ignore
      <h3
        className={cn(
          "step mt-8 scroll-m-20 font-semibold text-lg tracking-tight",
          className
        )}
        {...props}
      />
      // @end
    ),
    Image: lazy(() => import("@acme/ui/zoom-image")),
    callout: Callout,
    GridContainer: lazy(() => import("@acme/ui/grid-container")),
    LoadingSkeleton: lazy(() => import("@acme/ui/loading-skeleton")),
    ComponentPreview: lazy(() => import("@acme/ui/component-preview")),
    FolderTree: lazy(() => import("@acme/ui/folder-tree")),
    CodePlayground: lazy(() => import("@acme/ui/code-playground")),
    Youtube: lazy(() => import("@acme/ui/youtube")),
    files: Files,
    folder: Folder,
    file: File,
  };
